"use client";
import "@/styles/aboutOneContent.style.css";
import SectionTitle from "@/components/shared/sectionTitle";
import { Link } from "@/i18n/routing";
import Image from "next/image";
import Text from "@/components/shared/text";
import Flex from "@/components/core/flex";
import { getFilePath } from "@/lib/utils/utils";
import { ServiceSection } from "@/types/home";
import useLocalizer from "@/hooks/useLocalizer";

interface Props {
  data?: ServiceSection;
}

const ServiceContent = ({ data }: Props) => {
  const serviceSection = data;
  const { isRtl } = useLocalizer();
  if (!serviceSection) return null;

  return (
    <section className="relative block pt-64 pb-36 w-full min-h-[1000px] ">
      <div
        className="absolute inset-0 w-full h-[700px] bg-cover bg-center bg-fixed opacity-80  bg-blend-overlay"
        style={{
          backgroundImage: serviceSection?.banner
            ? `url('${getFilePath(serviceSection?.banner?.key ?? "")}')`
            : undefined,
        }}
      >
        <div className="w-full h-full bg-black/80 top-0 left-0 absolute"></div>
      </div>
      <div className="container mx-auto px-6 lg:px-0">
        <SectionTitle
          title={isRtl ? serviceSection?.arTitle : serviceSection?.enTitle}
          titleTag="h2"
          titleClassName="lg:text-3xl xl:text-5xl! text-white"
          description={
            isRtl
              ? serviceSection?.arDescription
              : serviceSection?.enDescription
          }
          lineStyle="2"
          descriptionClassName="text-white lg:text-3xl text-center"
          className="flex items-center justify-center mb-30"
        />
        {serviceSection?.services && serviceSection?.services?.length > 0 && (
          <div className="grid xl:grid-cols-3 gap-4 lg:gap-8 lg:px-30 items-center">
            {serviceSection?.services?.map((service) => (
              <ServiceCard
                key={service.id}
                imageUrl={getFilePath(service?.image?.key ?? "")}
                title={isRtl ? service?.arTitle : service?.enTitle}
                description={
                  (isRtl ? service?.arSubTitle : service?.enSubTitle) ?? ""
                }
                href={`services/${service.id}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

interface ServiceCardProps {
  imageUrl: string;
  title: string;
  description: string;
  href: string;
}
export const ServiceCard: React.FC<ServiceCardProps> = ({
  imageUrl,
  title,
  description,
  href,
}) => {
  return (
    <article className="w-full h-[520px] border-b border-r border-l border-primary/20 bg-white z-20 overflow-hidden transition-all duration-300 ease-in-out flex flex-col justify-between">
      <div className="relative overflow-hidden  h-[600px] w-full transition-all duration-300 ease-in-out hover:[&_.ser-top-cover]:h-full hover:[&_.ser-bottom-cover]:h-full hover:[&_.ser-small-img-frame]:p-0 ">
        <div className="absolute  w-full h-0 bg-black/20 -top-2/4 left-0 right-0 ser-top-cover z-20 transition-all duration-500 ease-linear" />
        <div className="absolute  w-full h-0 bg-black/20 -bottom-2/4 left-0 right-0 ser-bottom-cover z-20 transition-all duration-500 ease-linear" />
        <Image
          src={imageUrl}
          alt={title}
          fill
          className="object-cover"
          priority
          placeholder="empty"
        />
        <div className="absolute bg-secondary h-[106px] w-[106px] bottom-5 left-5 p-3 opacity-90 z-30 transition-all duration-500 ease-linear ser-small-img-frame">
          <div className="relative h-full w-full">
            <Image
              src={imageUrl}
              alt={title}
              fill
              priority
              placeholder="empty"
              className="object-cover ser-small-img"
            />
          </div>
        </div>
      </div>
      <div className="h-full">
        <Flex
          direction="col"
          spacing={{ all: 2 }}
          className="px-4 lg:px-4 py-2 lg:py-4 h-full w-full"
        >
          <Link href={href} aria-label={`${title}: ${description}`}>
            <Text
              as="h3"
              className="text-secondary font-bold line-clamp-1 lg:line-clamp-2 hover:text-primary "
            >
              {title}
            </Text>
          </Link>
          <Text className="text-secondary-foreground lg:line-clamp-3 line-clamp-2">
            {description}
          </Text>
        </Flex>
      </div>
    </article>
  );
};

export default ServiceContent;
